import {
  Code2,
  Globe,
  Smartphone,
  Users2,
  ClipboardList,
  Wallet,
  Cloud,
  type LucideIcon,
} from "lucide-react";
import type { ServicePageSlug } from "@/data/servicePageSlugs";

export interface ServiceItem {
  icon: LucideIcon;
  title: string;
  /** Element id of this card on /services, linked from the navbar submenu. */
  anchor: string;
  description: string;
  highlights: string[];
  /** Slug of this service's landing page (servicePages.ts), if it has one. */
  page?: ServicePageSlug;
  color: string;
  bg: string;
  border: string;
}

export interface ServiceCategory {
  /** Also the element id of the category's section on /services. */
  id: string;
  label: string;
  services: ServiceItem[];
}

export const SERVICE_CATEGORIES: ServiceCategory[] = [
  {
    id: "software-development",
    label: "Software Development",
    services: [
      {
        icon: Globe,
        title: "Web Development",
        anchor: "web-development",
        page: "web-development",
        description:
          "Custom websites, web applications and portals designed, built and maintained by our engineers — from a fast business website to a full-scale SaaS platform with APIs and admin dashboards.",
        highlights: [
          "Custom web applications & portals",
          "Business websites & landing pages",
          "E-commerce & payment integration",
          "React, Angular, Next.js & .NET",
          "Responsive, fast & SEO-ready builds",
        ],
        color: "text-cyan-600",
        bg: "bg-cyan-600/10",
        border: "border-cyan-600/20",
      },
      {
        icon: Smartphone,
        title: "App Development",
        anchor: "app-development",
        page: "mobile-app-development",
        description:
          "Mobile apps for Android and iOS that your customers and teams actually enjoy using — planned, designed, built, published to the app stores and supported after launch.",
        highlights: [
          "Android & iOS app development",
          "Cross-platform apps (React Native / Flutter)",
          "UI/UX design & prototyping",
          "Backend, APIs & admin panels",
          "App store publishing & ongoing support",
        ],
        color: "text-pink-600",
        bg: "bg-pink-600/10",
        border: "border-pink-600/20",
      },
    ],
  },
  {
    id: "technology",
    label: "Staff Augmentation & IT Consulting",
    services: [
      {
        icon: Code2,
        title: "Software Outsourcing & Modernization",
        anchor: "software-outsourcing",
        page: "dedicated-development-teams",
        description:
          "Extend your engineering capacity with experienced developers and dedicated development teams. We build, modernize and maintain software products for businesses that need reliable delivery without expanding their internal hiring.",
        highlights: [
          "Dedicated developers",
          "Dedicated development teams",
          "API & systems integration",
          "Application modernization",
          "Long-term maintenance & support",
        ],
        color: "text-accent",
        bg: "bg-accent/10",
        border: "border-accent/20",
      },
      {
        icon: Users2,
        title: "Dedicated Development Teams",
        anchor: "dedicated-development-teams",
        page: "dedicated-development-teams",
        description:
          "A ready engineering unit — developers, QA and a tech lead — that plugs into your project and scales up or down as your requirements change.",
        highlights: [
          "Individual developers or full teams",
          ".NET, Angular, React, Python engineers",
          "Technical evaluation before onboarding",
          "Flexible, month-to-month scaling",
        ],
        color: "text-blue-500",
        bg: "bg-blue-500/10",
        border: "border-blue-500/20",
      },
      {
        icon: Cloud,
        title: "IT Consulting & Cloud",
        anchor: "it-consulting-cloud",
        page: "it-consulting-cloud",
        description:
          "Architecture reviews, technology strategy and cloud/DevOps support for teams modernizing their infrastructure or planning a transformation roadmap.",
        highlights: [
          "Software architecture review",
          "Cloud & DevOps (Azure, AWS)",
          "Technology strategy & roadmapping",
          "CI/CD and infrastructure setup",
        ],
        color: "text-secondary",
        bg: "bg-secondary/10",
        border: "border-secondary/20",
      },
    ],
  },
  {
    id: "hr-business",
    label: "HR & Business Services",
    services: [
      {
        icon: ClipboardList,
        title: "HR Process Outsourcing",
        anchor: "hr-process-outsourcing",
        page: "hr-outsourcing-payroll",
        description:
          "End-to-end HR outsourcing covering the employee lifecycle — from recruitment and onboarding to performance management and offboarding.",
        highlights: [
          "Recruitment & talent acquisition",
          "Employee onboarding & offboarding",
          "Performance management systems",
          "HR policy development",
        ],
        color: "text-purple-500",
        bg: "bg-purple-500/10",
        border: "border-purple-500/20",
      },
      {
        icon: Wallet,
        title: "Payroll Management",
        anchor: "payroll-management",
        page: "hr-outsourcing-payroll",
        description:
          "Accurate, compliant payroll processing so you can focus on running your business instead of managing payroll administration.",
        highlights: [
          "Automated payroll processing",
          "Tax compliance & filings",
          "Statutory deductions management",
          "Payroll reporting & analytics",
        ],
        color: "text-green-600",
        bg: "bg-green-600/10",
        border: "border-green-600/20",
      },
    ],
  },
];

function servicesIn(categoryId: string) {
  return SERVICE_CATEGORIES.find((c) => c.id === categoryId)!.services;
}

// Compact preview on the homepage: software development plus the core
// engagement models.
export const HOMEPAGE_SERVICES = [
  ...servicesIn("software-development"),
  ...servicesIn("technology").filter((s) =>
    ["Dedicated Development Teams", "IT Consulting & Cloud"].includes(s.title)
  ),
];
